import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import "./UsageHistory.css";
import { isMockApi } from "../../../../services/api/client";
import {
  downloadCreationAttachment,
  downloadExportAttachment,
  getCreations,
  mergeDesignItems,
  MY_DESIGNS_FILE_TYPES,
} from "../../../../services/api/editor/background_removal";
import { saveBlobAsFile } from "../../../../services/api/editor/export";
import logger from "../../../../utils/logger";

const TYPE_LABELS = {
  image: "Bg remove",
  ai_background: "AI background",
  upscale: "Upscale",
  outpaint: "Outpaint",
  passport_photo: "Passport photo",
  download: "Export",
};

/** Best-effort credit cost by creation type (matches pricing catalog defaults). */
const TYPE_CREDITS = {
  image: 1,
  ai_background: 1,
  upscale: 3,
  outpaint: 3,
  passport_photo: 5,
  download: 0,
};

const MOCK_PREVIEW_ROWS = [
  {
    key: "mock-1",
    date: formatUsageDate(new Date(Date.now() - 1 * 3600000).toISOString()),
    type: "Bg remove",
    used: "1",
    status: "Success",
    canDownload: false,
  },
  {
    key: "mock-2",
    date: formatUsageDate(new Date(Date.now() - 5 * 3600000).toISOString()),
    type: "AI background",
    used: "1",
    status: "Success",
    canDownload: false,
  },
  {
    key: "mock-3",
    date: formatUsageDate(new Date(Date.now() - 26 * 3600000).toISOString()),
    type: "Upscale",
    used: "3",
    status: "Success",
    canDownload: false,
  },
  {
    key: "mock-4",
    date: formatUsageDate(new Date(Date.now() - 30 * 3600000).toISOString()),
    type: "Bg remove",
    used: "0",
    status: "Failed",
    canDownload: false,
  },
];

function formatUsageDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

const typeLabel = (fileType) => {
  const key = String(fileType || "").toLowerCase();
  if (TYPE_LABELS[key]) return TYPE_LABELS[key];
  if (!key) return "Tool use";
  return key
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const creditsUsed = (fileType) => {
  const key = String(fileType || "").toLowerCase();
  if (Object.prototype.hasOwnProperty.call(TYPE_CREDITS, key)) {
    return String(TYPE_CREDITS[key]);
  }
  return "—";
};

const designNumericId = (item) => {
  if (item.kind === "export") {
    return item.export_id ?? Number(String(item.id).replace(/^export:/, ""));
  }
  return item.creation_id ?? Number(String(item.id).replace(/^creation:/, ""));
};

const downloadFileName = (item) => {
  const ext = item.format || item.output_format || item.file_ext || "png";
  const rawName =
    item.file_name || `${item.kind || "creation"}-${designNumericId(item) || "file"}`;
  const baseName = String(rawName).replace(/\.[^./\\]+$/, "");
  return `${baseName}.${ext}`;
};

const mapUsageRows = (items) =>
  items
    .filter((item) => item?.file_type !== "library_upload")
    .map((item, index) => {
      const hasOutput = Boolean(item.output_url || item.output_file_key);
      const id = designNumericId(item);
      return {
        key: `${item.kind || "creation"}-${id || index}-${item.created_at || index}`,
        date: formatUsageDate(item.created_at),
        type: typeLabel(item.file_type),
        used: creditsUsed(item.file_type),
        status: hasOutput || id ? "Success" : "Failed",
        canDownload: Boolean(id) && (hasOutput || item.kind === "export"),
        kind: item.kind === "export" ? "export" : "creation",
        id,
        fileName: downloadFileName(item),
        outputUrl: item.output_url,
      };
    });

const UsageHistory = () => {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [downloadingKey, setDownloadingKey] = useState("");

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const token = await getToken();
      if (!token) {
        setRows([]);
        setError("Sign in to view usage history.");
        return;
      }

      const response = await getCreations(token, {
        skip: 0,
        limit: 50,
        fileTypes: MY_DESIGNS_FILE_TYPES,
        includeExports: true,
      });

      if (!response?.success && !Array.isArray(response?.creations)) {
        throw new Error(response?.message || "Failed to load usage history");
      }

      const items = mergeDesignItems(response);
      const mapped = mapUsageRows(items);
      setRows(
        mapped.length > 0
          ? mapped
          : isMockApi
            ? MOCK_PREVIEW_ROWS
            : []
      );
    } catch (err) {
      logger.error("Failed to load usage history", err);
      setRows([]);
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Couldn’t load usage history."
      );
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleDownload = async (row) => {
    if (!row?.canDownload || !row.id || downloadingKey) return;

    setDownloadingKey(row.key);
    try {
      const token = await getToken();
      if (!token) throw new Error("Not signed in");

      if (row.outputUrl?.startsWith("blob:")) {
        const link = document.createElement("a");
        link.href = row.outputUrl;
        link.download = row.fileName;
        link.click();
        return;
      }

      const blob =
        row.kind === "export"
          ? await downloadExportAttachment(token, row.id)
          : await downloadCreationAttachment(token, row.id);
      saveBlobAsFile(blob, row.fileName);
    } catch (err) {
      logger.error("Failed to download usage result", err);
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Couldn’t download that file."
      );
    } finally {
      setDownloadingKey("");
    }
  };

  const headerStatus = loading
    ? "Loading usage history…"
    : error
      ? error
      : rows.length === 0
        ? "No tool runs yet"
        : `${rows.length} record${rows.length === 1 ? "" : "s"}`;

  const headerStatusTone = error ? "error" : "muted";

  return (
    <div className="usage-container">
      <div className="usage-heading">
        <h2 className="usage-title">Usage History</h2>
        <p
          className={`usage-header-status usage-header-status--${headerStatusTone}`}
          role="status"
          aria-live="polite"
        >
          {headerStatus || "\u00A0"}
        </p>
      </div>

      <div
        className={`usage-table-wrapper${loading ? " is-loading" : ""}`}
        aria-busy={loading}
      >
        {loading && rows.length === 0 ? (
          <div className="usage-empty">Fetching your recent tool runs…</div>
        ) : !loading && rows.length === 0 ? (
          <div className="usage-empty">
            <p className="usage-empty__title">No usage history yet</p>
            <p className="usage-empty__copy">
              Background removals, AI tools, and exports will show up here after
              you run them.
            </p>
          </div>
        ) : (
          <table className="usage-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Used</th>
                <th>Status</th>
                <th>Download</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((entry) => (
                <tr className="usage-row" key={entry.key}>
                  <td>{entry.date}</td>
                  <td>{entry.type}</td>
                  <td>{entry.used}</td>
                  <td>
                    <span
                      className={`usage-status usage-status--${String(
                        entry.status
                      ).toLowerCase()}`}
                    >
                      {entry.status}
                    </span>
                  </td>
                  <td>
                    {entry.canDownload ? (
                      <button
                        type="button"
                        className="usage-result-link"
                        onClick={() => handleDownload(entry)}
                        disabled={downloadingKey === entry.key}
                      >
                        {downloadingKey === entry.key
                          ? "Downloading…"
                          : "Download"}
                      </button>
                    ) : (
                      <span className="usage-result-na">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default UsageHistory;
